import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    AlarmClock,
    Bell,
    CheckCircle2,
    MessageSquare,
    PencilLine,
    UserPlus,
} from 'lucide-react';
import {
    useNotifications,
    useMarkNotificationAsRead,
    useMarkAllNotificationsAsRead,
} from '../hooks/useApi';
import {
    Badge,
    Button,
    Card,
    EmptyState,
    ListSkeleton,
    PageHeader,
    Tabs,
} from '../components/ui';

const formatRelativeTime = (date) => {
    const diffInSeconds = Math.floor((Date.now() - date) / 1000);
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
    if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
    if (diffInSeconds < 604800) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
    return date.toLocaleDateString();
};

const TYPE_META = {
    assignment: { icon: UserPlus, tone: 'bg-info-subtle text-info-strong', badge: 'info' },
    deadline: { icon: AlarmClock, tone: 'bg-warning-subtle text-warning-strong', badge: 'warning' },
    update: { icon: PencilLine, tone: 'bg-ink-100 text-ink-600', badge: 'neutral' },
    comment: { icon: MessageSquare, tone: 'bg-primary-50 text-primary-600', badge: 'primary' },
    approval: { icon: CheckCircle2, tone: 'bg-success-subtle text-success-strong', badge: 'success' },
};

const Notifications = () => {
    const { data: notificationsData, isLoading } = useNotifications();
    const markAsRead = useMarkNotificationAsRead();
    const markAllAsRead = useMarkAllNotificationsAsRead();
    const [tab, setTab] = useState('all');

    const notifications = useMemo(() => notificationsData?.data || [], [notificationsData]);
    const unreadCount = notificationsData?.unread_count || 0;

    const visible = useMemo(
        () => (tab === 'unread' ? notifications.filter((n) => !n.is_read) : notifications),
        [notifications, tab]
    );

    const getNotificationLink = (n) => {
        switch (n.entity_type) {
            case 'task':
                return `/tasks/${n.entity_id}`;
            case 'project':
                return `/projects/${n.entity_id}`;
            case 'meeting':
                return `/meetings/${n.entity_id}`;
            case 'ticket':
                return `/tickets/${n.entity_id}`;
            default:
                return null;
        }
    };

    return (
        <div className="max-w-3xl">
            <PageHeader
                title="Notifications"
                description="Assignments, deadline reminders and updates across your work."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Notifications' }]}
                actions={
                    unreadCount > 0 && (
                        <Button onClick={() => markAllAsRead.mutate()} loading={markAllAsRead.isPending}>
                            Mark all read
                        </Button>
                    )
                }
            />

            <Card>
                <div className="px-4 pt-1">
                    <Tabs
                        value={tab}
                        onChange={setTab}
                        tabs={[
                            { value: 'all', label: 'All', count: notifications.length },
                            { value: 'unread', label: 'Unread', count: unreadCount },
                        ]}
                    />
                </div>

                {isLoading ? (
                    <div className="p-5">
                        <ListSkeleton rows={5} />
                    </div>
                ) : visible.length === 0 ? (
                    <EmptyState
                        icon={Bell}
                        title={tab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                        description={
                            tab === 'unread'
                                ? "You've read everything. New items will show up here."
                                : "We'll notify you when something needs your attention."
                        }
                    />
                ) : (
                    <ul className="divide-y divide-[var(--divider-color)]">
                        {visible.map((n) => {
                            const meta = TYPE_META[n.type] || {
                                icon: Bell,
                                tone: 'bg-ink-100 text-ink-600',
                                badge: 'neutral',
                            };
                            const Icon = meta.icon;
                            const href = getNotificationLink(n);

                            return (
                                <li
                                    key={n.id}
                                    className={`flex gap-3 px-4 py-3.5 ${!n.is_read ? 'bg-primary-50/30' : ''}`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${meta.tone}`}
                                    >
                                        <Icon size={15} />
                                    </span>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start justify-between gap-3">
                                            {href ? (
                                                <Link
                                                    to={href}
                                                    onClick={() => !n.is_read && markAsRead.mutate(n.id)}
                                                    className={`text-md hover:text-primary-600 transition-colors duration-150 ${!n.is_read ? 'font-semibold text-ink-900' : 'text-ink-800'
                                                        }`}
                                                >
                                                    {n.title}
                                                </Link>
                                            ) : (
                                                <span
                                                    className={`text-md ${!n.is_read ? 'font-semibold text-ink-900' : 'text-ink-800'
                                                        }`}
                                                >
                                                    {n.title}
                                                </span>
                                            )}

                                            {!n.is_read && (
                                                <button
                                                    type="button"
                                                    onClick={() => markAsRead.mutate(n.id)}
                                                    className="shrink-0 text-label font-medium text-secondary-600 hover:text-secondary-700 transition-colors duration-150"
                                                >
                                                    Mark read
                                                </button>
                                            )}
                                        </div>

                                        <p className="text-base text-ink-600 mt-0.5 leading-relaxed">
                                            {n.message}
                                        </p>

                                        <div className="flex items-center gap-2 mt-2">
                                            <Badge variant={meta.badge}>{n.type}</Badge>
                                            <span className="text-micro text-ink-400">
                                                {formatRelativeTime(new Date(n.created_at))}
                                            </span>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Card>
        </div>
    );
};

export default Notifications;
